import React, { Component } from 'react';
import { connect } from 'react-redux';
import { get, orderBy } from 'lodash';

import { getActivitiesSettings } from 'helpers/objectiveSettings';
import { fetchMeta as fetchEntityMeta } from 'services/entities/duck';
import { fetchTactics as fetchChannels } from 'services/tactics/duck';
import { fetchCampaign, fetchCampaignCallouts, fetchActiveChannels } from 'services/campaign/duck';
import { fetchActivities } from 'services/activities/duck';
import { generateCalloutComponents } from 'helpers/componentGenerators';
import { shouldShowTotalActivities } from 'helpers/data';
import { validateStatisticsData } from 'helpers/validate';
import { setObjectiveShadow } from 'services/config/duck';

// Components and Containers
import { Grid, Row, Col } from 'react-bootstrap';
import { NoChannelDataTile } from 'components/molecules/ErrorTile';
import RenderingTile from 'components/molecules/RenderingTile';
import ChannelTitle from 'components/atoms/layout/ChannelTitle';
import ExportPDF from 'containers/buttons/ExportPDF';
import CreateNewNote from 'containers/buttons/CreateNewNote';
import ViewEntity from 'containers/buttons/ViewEntity';
import Note from 'containers/annotations/NoteContainer';
import TileWithValidation from 'components/molecules/TileWithValidation';
import ObjectiveSwitcher from 'components/molecules/ObjectiveSwitcher';

// Call Outs
import CallOutValueWithTreemap from 'components/molecules/CallOutValueWithTreemap';
import ChannelSummaryCallouts from 'components/molecules/ChannelSummaryCallouts';

// Charts
import ActivitiesStackedAreaChart from 'components/molecules/charts/ActivitiesStackedAreaChart';
import GroupedBarChartWithCategories from 'components/molecules/charts/GroupedBarChartWithCategories';
import ChannelStackedChart from 'components/molecules/charts/ChannelStackedChart';
import ChannelDonutChart from 'components/molecules/charts/ChannelDonutChart';

// Tables
import ActivitiesPivotTable from 'components/molecules/tables/ActivitiesPivotTable';
import ChannelOverviewTable from 'components/molecules/tables/ChannelOverviewTable';


const emptyArray = [];

class LayerThreeSummaryContainer extends Component {
    constructor(props) {
        super(props);
        this.state = { entityId: null };
    }

    static getDerivedStateFromProps(props, state) {
        const entityId = parseInt(props.match.params.entityId, 10);
        if (entityId !== state.entityId) {
            return {
                ...state,
                entityId
            };
        }

        return null;
    }

    componentDidMount() {
        this.makeRequestsForData();
    }

    createPayload(entityId) {
        return {
            transactionId: `entity-${entityId}-layer-two-summary`,
            entityId
        };
    }

    makeRequestsForData() {
        const entityId = this.state.entityId;
        const payload = this.createPayload(entityId);

        // HACK: Only trigger an API call if the data has not been previously pulled
        if (this.props.entities.meta.id !== entityId) {
            this.props.fetchEntityMeta(payload);
        }

        this.props.fetchChannels(payload);
        this.props.fetchCampaign(payload);
        this.props.fetchCampaignCallouts(payload);
        this.props.fetchActivities(payload);
        this.props.fetchActiveChannels(payload);
    }

    componentDidUpdate(prevProps, prevState) {
        const isEntityChanged = prevState.entityId !== this.state.entityId;
        const isTimeRangeChanged = prevProps.selectedTimeRange !== this.props.selectedTimeRange;
        if (isTimeRangeChanged || isEntityChanged) {
            this.makeRequestsForData();
        }
    }

    getCurrentEntityId() {
        return this.state.entityId;
    }

    render() {
        const stub = 'l3-summary';
        const {
            enabledChannels,
            xAxisLabel,
            backfillBuckets,
            entityObjective,
            entityObjectiveLabelCode,
            showTotalActivities,
            selectedTimeRange,
            hideBudgetTimeCharts,
            showeCPAonSummaryDashboard,
            showConversionRevenue,
            showSocialCpconv
        } = this.props;

        const {
            isLabelCoded,
            eCPAActivitiesOrClicks,
            eCPAActivitiesOrClicksTerm,
            primaryActivityFieldTerm,
            showActivitiesData,
            primaryActivityField,
            singularTerm,
            pluralTerm,
            fullObjectiveTerm,
            helpKeySuffix,
            ecpaTerm
        } = getActivitiesSettings(entityObjective, entityObjectiveLabelCode);

        const hasSummaryData = get(this.props, 'summary.hasData', null);
        const hasCalloutData = get(this.props, 'summary.callouts.params.hasData', null);
        const hasActiveChannelsData = get(this.props, 'summary.activeChannels.params.hasData', null);
        const callouts = get(this.props, 'summary.callouts.data', {});
        const calloutWindow = get(this.props, 'summary.callouts.params.timeRanges.previous.windowSize', null);
        const activeChannelData = get(this.props, 'summary.activeChannels.data', {});
        const allChannelsList = get(this.props, 'summary.activeChannels.channels', emptyArray);
        const activeChannels = allChannelsList.reduce((prev, channel) => {
            // Active Channel is defined to be a channel with a non-zero imprerssion
            // count for a given time window.
            if (activeChannelData[channel].impressions > 0) {
                prev.push(channel);
            }
            return prev;
        }, []);
        const activeChannelsCount = activeChannels.length;
        const channelOptions = {
            social: { showSocialCpconv }
        };

        const hasChannelsData = get(this.props, 'channels.hasData', null);
        const hasChannelInsightsData = get(this.props, 'channels.insights', null);
        const channelOverviewTableData = [];
        let channels = [];

        if (hasChannelInsightsData) {
            channels = Object.keys(this.props.channels.data);
            // create channel overview table data
            channels.forEach((channel) => {
                channelOverviewTableData.push({
                    channel, ...this.props.channels.data[channel].statistics
                });
            });
        }

        const activities = get(this.props, 'activities', {});
        const rankings = get(activities, 'rankings', {});
        const hasActivitiesData = get(activities, 'hasData', null);

        let activitiesData;
        if (hasActivitiesData) {
            activitiesData = Object.entries(activities.data).reduce((prev, entry) => {
                const [name, data] = entry;
                prev.push({ name, ...data.statistics });
                return prev;
            }, []);
        }
        const sortedActivitiesData =
            hasActivitiesData
                ? orderBy(activitiesData, [entityObjective === 'ctr' ? 'total_activities' : `${entityObjective}_activities`], ['desc'])
                : [];
        const activitiesRenderFlag = hasActivitiesData && (showActivitiesData || showTotalActivities);

        const calloutSettings = [
            { key: 'impressions', title: 'Impressions', format: '0,0' },
            {
                key: eCPAActivitiesOrClicks,
                helpKey: `${eCPAActivitiesOrClicks}${helpKeySuffix}`,
                title: eCPAActivitiesOrClicksTerm,
                format: '0,0'
            },
            showeCPAonSummaryDashboard ? {
                key: 'ecpa',
                helpKey: `ecpa${helpKeySuffix}`,
                title: entityObjectiveLabelCode ? `Average ${ecpaTerm}` : 'eCPA',
                format: '$0,0.00'
            } : null,
            {
                component: CallOutValueWithTreemap,
                props: {
                    exportName: 'l3-channels-live',
                    title: 'Channels Live',
                    data: {
                        value: activeChannelsCount,
                        treeMap: activeChannelData,
                        channels: activeChannels,
                        accessor: 'spend'
                    },
                    enabledChannels,
                    format: 'number'
                },
                hasData: hasActiveChannelsData
            }
        ].filter(e => e);

        const calloutComponents = generateCalloutComponents({
            stub, calloutSettings, hasCalloutData, callouts, calloutWindow, isLabelCoded
        });

        // If we have data to render, let us render!
        const hasData = [
            hasSummaryData === null ? true : hasSummaryData,
            hasCalloutData && hasChannelsData,
            hasChannelsData,
            hasChannelInsightsData,
            hasActivitiesData
        ].some(d => d);

        const ChannelActions = <React.Fragment>
            <CreateNewNote />
            <ViewEntity />
            {hasData && <ExportPDF template={stub} />}
        </React.Fragment>;

        return hasData ? (
            <Grid fluid>
                <ChannelTitle print={ChannelActions}>
                    Summary
                </ChannelTitle>
                {this.props.enableObjectiveSwitching && <ObjectiveSwitcher
                    setObjectiveShadow={this.props.setObjectiveShadow}
                    fetchEntityMeta={this.props.fetchEntityMeta}
                    entityId={this.getCurrentEntityId()}
                    objective={this.props.entityObjective}
                    objectiveLabelCode={this.props.entityObjectiveLabelCode}
                />}
                <Note
                    dashboard="summary"
                    entityId={this.getCurrentEntityId()}
                    start={selectedTimeRange.apiConfig.start().format('YYYY-MM-DD')}
                    end={selectedTimeRange.apiConfig.end().format('YYYY-MM-DD')}
                />
                {/* Call Outs */}
                <Row>
                    {calloutComponents}
                </Row>

                <ChannelSummaryCallouts
                    channelsList={allChannelsList}
                    entityId={this.getCurrentEntityId()}
                    layer="l3"
                    channelOptions={channelOptions}
                />

                {/* Activities Chart */}
                {activitiesRenderFlag && !this.props.enforceSummaryOnlyDashboardView ? (
                    <GroupedBarChartWithCategories
                        title={`TOP ${pluralTerm.toUpperCase()}`}
                        exportName={`${stub}-top-activities`}
                        data={sortedActivitiesData}
                        showTopOnly
                        filterField={entityObjective !== 'ctr' ? 'total_activities' : primaryActivityField}
                        keys={[
                            entityObjective !== 'ctr' ? { key: `${entityObjective}_activities`, title: fullObjectiveTerm } : null,
                            showTotalActivities ? { key: 'total_activities', title: `Total ${pluralTerm}` } : null
                        ].filter(e => e)}
                        partitionBy="name"
                        animateDuration={2000} />
                ) : (
                    <RenderingTile height='475px' show={activitiesRenderFlag && !this.props.enforceSummaryOnlyDashboardView} />
                )}

                {/* Donut Charts */}
                <Row>
                    <Col md={6}>
                        {hasChannelInsightsData ? (
                            <ChannelDonutChart
                                title='IMPRESSIONS BY CHANNEL'
                                exportName="l3-left-donut"
                                enabledChannels={enabledChannels}
                                data={get(this.props, 'channels.insights.percentages.impressions', {})}
                                y="impressions"
                                total={get(this.props, 'channels.insights.totals.impressions', 0)}
                                totalTitle="Total Impressions"
                                totalFormat='0,0'
                                animateDuration={2000} />
                        ) : (
                            <RenderingTile height='465px' show={hasChannelInsightsData} />
                        )}
                    </Col>
                    <Col md={6}>
                        <TileWithValidation
                            hasData={hasChannelInsightsData}
                            innerComponent={ChannelDonutChart}
                            height='430px'
                            innerProps={{
                                title: `${eCPAActivitiesOrClicksTerm.toUpperCase()} BY CHANNEL`,
                                exportName: 'l3-right-donut',
                                data: get(this.props, `channels.insights.percentages.${eCPAActivitiesOrClicks}`, {}),
                                enabledChannels,
                                total: get(this.props, `channels.insights.totals.${eCPAActivitiesOrClicks}`, 0),
                                totalTitle: eCPAActivitiesOrClicksTerm,
                                totalFormat: '0,0',
                                y: eCPAActivitiesOrClicks,
                                animateDuration: 2000,
                                validation: {
                                    key: eCPAActivitiesOrClicks,
                                    data: get(this.props, 'channels.insights.totals', {}),
                                    validator: validateStatisticsData
                                }
                            }}
                        />
                    </Col>
                </Row>
                {/* Charts */}

                {/* Channel Overview Table React-Table*/}
                {hasChannelsData ? (
                    <React.Fragment>
                        {hasChannelInsightsData ? (
                            <ChannelOverviewTable
                                title="CHANNEL OVERVIEW"
                                exportName={`${stub}-channel-overview`}
                                entityId={this.props.entities.meta.id}
                                data={channelOverviewTableData}
                                showTotalActivities={showTotalActivities}
                                disableChannelLinks={this.props.enforceSummaryOnlyDashboardView}
                                showeCPA={showeCPAonSummaryDashboard}
                                showConversionRevenue={showConversionRevenue}
                                entityObjectiveLabelCode={entityObjectiveLabelCode}
                                accessors={
                                    entityObjective === 'ctr' ?
                                        ['impressions', 'clicks', 'ctr', 'spend'] :
                                        undefined
                                }
                            />
                        ) : (
                            <RenderingTile height='330px' show={hasChannelInsightsData} />
                        )}
                    </React.Fragment>
                ) : null}

                {activitiesRenderFlag && !this.props.enforceSummaryOnlyDashboardView ? (
                    <ActivitiesStackedAreaChart
                        title={`${singularTerm.toUpperCase()} TRENDS`}
                        exportName={`${stub}-activity-trends`}
                        exportLimit={10}
                        data={activities.data}
                        keys={rankings[primaryActivityField].map(d => d.name).reverse()}
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        yAxisLabel={primaryActivityFieldTerm}
                        y={primaryActivityField}
                        colors={this.props.theme.orderedColors}
                        animateDuration={2000} />
                ) : (
                    <RenderingTile height='400px' show={activitiesRenderFlag && !this.props.enforceSummaryOnlyDashboardView} />
                )}

                {activitiesRenderFlag && !this.props.enforceSummaryOnlyDashboardView ? (
                    <React.Fragment>
                        <ActivitiesPivotTable
                            title={`${singularTerm.toUpperCase()} BREAKDOWN BY CHANNEL`}
                            exportName={`${stub}-website-activity-breakdown`}
                            data={activities}
                            showTotalActivities={showTotalActivities}
                            entityObjectiveLabelCode={entityObjectiveLabelCode}
                            objectives={[entityObjective]}
                            filterField={showTotalActivities ? 'total_activities' : primaryActivityField}
                        />
                    </React.Fragment>
                ) : (
                    <RenderingTile height='400px' show={activitiesRenderFlag && !this.props.enforceSummaryOnlyDashboardView} />
                )}

                {hasChannelsData ? (
                    <ChannelStackedChart
                        title="IMPRESSIONS BY CHANNEL"
                        exportName={`${stub}-impressions-by-channel`}
                        chart={{ type: 'area', zoomType: 'xy' }}
                        data={this.props.channels.groupedData}
                        enabledChannels={enabledChannels}
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        y="impressions"
                        animateDuration={2000} />
                ) : (
                    <RenderingTile height='400px' show={hasChannelsData} />
                )}

                <TileWithValidation
                    hasData={hasChannelsData}
                    innerComponent={ChannelStackedChart}
                    innerProps={{
                        title: `${eCPAActivitiesOrClicksTerm.toUpperCase()} BY CHANNEL`,
                        exportName: `${stub}-kpi-by-channel`,
                        chart: { type: 'area', zoomType: 'xy' },
                        data: get(this.props, 'channels.groupedData', {}),
                        enabledChannels,
                        y: eCPAActivitiesOrClicks,
                        yAxisLabel: eCPAActivitiesOrClicksTerm,
                        xAxisLabel,
                        backfillBuckets,
                        animateDuration: 2000,
                        validation: {
                            key: eCPAActivitiesOrClicks,
                            data: get(this.props, 'channels.insights.totals', {}),
                            validator: validateStatisticsData
                        }
                    }}
                />

                {hasChannelsData && !hideBudgetTimeCharts ? (
                    <ChannelStackedChart
                        title='BUDGET BY CHANNEL'
                        exportName={`${stub}-budget-by-channel`}
                        chart={{ type: 'column' }}
                        isMoney={true}
                        data={this.props.channels.groupedData}
                        enabledChannels={enabledChannels}
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        yAxisLabel='Budget'
                        y="spend"
                        animateDuration={2000} />
                ) : (
                    <RenderingTile height='400px' show={hasChannelsData && !hideBudgetTimeCharts} />
                )}
            </Grid>
        ) : (
            <Grid fluid={true}>
                <ChannelTitle print={ChannelActions}>
                    Summary
                </ChannelTitle>
                <Note
                    dashboard="summary"
                    entityId={this.getCurrentEntityId()}
                    start={selectedTimeRange.apiConfig.start().format('YYYY-MM-DD')}
                    end={selectedTimeRange.apiConfig.end().format('YYYY-MM-DD')}
                />
                <NoChannelDataTile channel='Summary' />
            </Grid>
        );
    }
}

const mapStateToProps = state => ({
    theme: state.theme,
    entities: state.entities,
    enableObjectiveSwitching: get(state, 'entities.meta.meta.enableObjectiveSwitching', false),
    enabledChannels: state.entities.meta.enabledChannels,
    entityObjective: get(state, 'entities.meta.meta.programmaticObjective.global', 'ecpa'),
    entityObjectiveLabelCode: get(state, 'entities.meta.meta.programmaticObjective.labelCode', null),
    showTotalActivities: shouldShowTotalActivities(state),
    hideBudgetTimeCharts: get(state, 'entities.meta.meta.hideBudgetTimeCharts', false),
    showeCPAonSummaryDashboard: get(state, 'entities.meta.meta.showeCPAonSummaryDashboard', false),
    showConversionRevenue: get(state, 'entities.meta.meta.showConversionRevenue', false),
    showSocialCpconv: get(state, 'entities.meta.meta.showSocialCpconv', false),
    enforceSummaryOnlyDashboardView: get(state, 'entities.meta.meta.enforceSummaryOnlyDashboardView', false),
    // TODO: Refactor to Channels, not Tactics
    channels: state.tactics,
    creatives: state.creatives,
    activities: state.activities,
    // TODO: Rename to 'summary' throughout
    summary: state.campaign,
    selectedTimeRange: state.config.selectedTimeRange,
    xAxisLabel: get(state, 'config.timeBuckets.config.xAxisLabel', 'Date'),
    backfillBuckets: get(state, 'config.timeBuckets.config.buckets', []),
});

export default connect(mapStateToProps, {
    fetchActiveChannels,
    fetchActivities,
    fetchEntityMeta,
    fetchChannels,
    fetchCampaign,
    fetchCampaignCallouts,
    setObjectiveShadow,
})(LayerThreeSummaryContainer);



// WEBPACK FOOTER //
// ./src/containers/dashboards/LayerThreeSummaryContainer.js